import { DEFAULT_ACCENT, GROUND, TEXT_GROUND } from './palette';

export function isHex6(s: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(s);
}
function channel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
function rgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
function toHsl(hex: string): [number, number, number] {
  const [r0, g0, b0] = rgb(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r0, g0, b0);
  const min = Math.min(r0, g0, b0);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r0) h = ((g0 - b0) / d + (g0 < b0 ? 6 : 0)) / 6;
  else if (max === g0) h = ((b0 - r0) / d + 2) / 6;
  else h = ((r0 - g0) / d + 4) / 6;
  return [h, s, l];
}
function fromHsl(h: number, s: number, l: number): string {
  const f = (n: number): number => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  const toHex = (v: number): string =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
export function ensureContrast(
  accent: string,
  ground: string,
  min = 4.5,
): { hex: string; adjusted: boolean } {
  if (contrastRatio(accent, ground) >= min) return { hex: accent, adjusted: false };
  const darkenTowards = luminance(ground) > 0.5;
  const [h, s, startL] = toHsl(accent);
  let l = startL;
  for (let i = 0; i < 50; i++) {
    l = darkenTowards ? Math.max(0, l - 0.02) : Math.min(1, l + 0.02);
    const hex = fromHsl(h, s, l);
    if (contrastRatio(hex, ground) >= min) return { hex, adjusted: true };
  }
  return { hex: darkenTowards ? '#121316' : '#E9E9E7', adjusted: true };
}

/** Black or white, whichever remains readable on this opaque fill. */
export function onColor(fill: string): string {
  return contrastRatio('#FFFFFF', fill) >= contrastRatio('#000000', fill) ? '#FFFFFF' : '#000000';
}

/** Direction AC's dark label on a filled accent: the near-black page ground, not pure black. */
const ON_ACCENT_DARK = GROUND.dark;

/** Mix toward black (negative) or white (positive). */
function shade(hex: string, amount: number): string {
  const to = amount < 0 ? 0 : 255;
  return `#${rgb(hex)
    .map((v) =>
      Math.round(v + (to - v) * Math.abs(amount))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase()}`;
}

/** Shared by the tenant shell and its live preview. Fills and text have different grounds. */
export function accentColors(requested: string, theme: 'light' | 'dark') {
  const light = theme === 'light';
  const scheme = light ? 'light' : 'dark';
  const valid = isHex6(requested) ? requested : DEFAULT_ACCENT[scheme];
  const fill = ensureContrast(valid, GROUND[scheme]).hex;
  // Accent text sits on the page ground, the white card, or the accent tint behind an in-progress
  // row; the tint (light) and the raised chip (dark) are the hardest grounds. See shared/palette.ts.
  const textGround = TEXT_GROUND[scheme];
  const text = ensureContrast(fill, textGround).hex;
  const onFill = onColor(fill);
  // A fill that needs a dark label takes the AC near-black instead of pure black. The dark fill
  // already clears 4.5:1 against that ground (ensureContrast above), and the check keeps any other
  // case on black.
  const label = onFill === '#000000' && contrastRatio(ON_ACCENT_DARK, fill) >= 4.5 ? ON_ACCENT_DARK : onFill;
  return {
    '--accent': fill,
    '--aurora': fill,
    '--accent-text': text,
    '--accent-d': ensureContrast(shade(fill, -0.15), textGround, 3).hex,
    '--accent-hover': shade(fill, onFill === '#FFFFFF' ? -0.1 : 0.1),
    '--on-accent': label,
    '--glow': `rgba(${rgb(fill).join(', ')}, ${light ? 0.2 : 0.35})`,
  };
}
